// i18n 工具函數
import { messages, type NestedTranslations, type TranslationValue } from './messages'
import { newToolsTranslations } from './newToolsTranslations'

// 支援的語系列表
export const SUPPORTED_LOCALES = ['zh-TW', 'zh-CN', 'en', 'hi', 'ja', 'ko'] as const
export type SupportedLocale = typeof SUPPORTED_LOCALES[number]

// 合併新工具翻譯到 messages.tools
function getMergedMessages(): NestedTranslations {
  const mergedMessages = { ...messages }

  // 確保 tools 存在
  if (!mergedMessages.tools) {
    mergedMessages.tools = {}
  }

  // 將新工具翻譯合併到 tools 下
  for (const [key, value] of Object.entries(newToolsTranslations)) {
    (mergedMessages.tools as any)[key] = value
  }

  return mergedMessages
}

// 合併後的 messages
const mergedMessages = getMergedMessages()

/**
 * 從統一翻譯結構中提取特定語系的翻譯
 * @param locale 目標語系
 * @param source 翻譯源資料
 * @returns 該語系的翻譯物件
 */
export function extractLocaleMessages(
  locale: SupportedLocale,
  source: NestedTranslations = mergedMessages
): Record<string, any> {
  const result: Record<string, any> = {}
  
  for (const [key, value] of Object.entries(source)) {
    if (isTranslationValue(value)) {
      result[key] = value[locale]
    } else {
      result[key] = extractLocaleMessages(locale, value)
    }
  }
  
  return result
}

/**
 * 檢查是否為翻譯值物件
 */
function isTranslationValue(value: any): value is TranslationValue {
  return typeof value === 'object' && 
         value !== null && 
         SUPPORTED_LOCALES.every(locale => locale in value)
}

/**
 * 檢查翻譯完整性 - 找出遺漏的翻譯
 * @param source 翻譯源資料
 * @returns 遺漏翻譯的報告
 */
export function checkTranslationCompleteness(
  source: NestedTranslations = mergedMessages
): TranslationCompletenessReport {
  const report: TranslationCompletenessReport = {
    totalKeys: 0,
    missingTranslations: {} as Record<SupportedLocale, string[]>,
    completenessRatio: {} as Record<SupportedLocale, number>
  }
  
  // 初始化每個語系的遺漏列表
  SUPPORTED_LOCALES.forEach(locale => {
    report.missingTranslations[locale] = []
    report.completenessRatio[locale] = 0
  })
  
  function checkNestedTranslations(obj: NestedTranslations, path: string = '') {
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key
      
      if (isTranslationValue(value)) {
        report.totalKeys++
        
        // 檢查每個語系是否有遺漏
        SUPPORTED_LOCALES.forEach(locale => {
          if (!value[locale] || value[locale].trim() === '') {
            report.missingTranslations[locale].push(fullPath)
          }
        })
      } else {
        checkNestedTranslations(value, fullPath)
      }
    }
  }
  
  checkNestedTranslations(source)
  
  // 計算完整性比例
  SUPPORTED_LOCALES.forEach(locale => {
    const missingCount = report.missingTranslations[locale].length
    report.completenessRatio[locale] = 
      report.totalKeys > 0 ? (report.totalKeys - missingCount) / report.totalKeys : 1
  })
  
  return report
}

/**
 * 翻譯完整性報告介面
 */
export interface TranslationCompletenessReport {
  totalKeys: number
  missingTranslations: Record<SupportedLocale, string[]>
  completenessRatio: Record<SupportedLocale, number>
}

/**
 * 生成翻譯完整性報告
 */
export function generateCompletenessReport(): string {
  const report = checkTranslationCompleteness()
  let output = '# 翻譯完整性報告\n\n'
  
  output += `## 總體統計\n`
  output += `- 總翻譯鍵值數: ${report.totalKeys}\n\n`
  
  output += `## 各語系完整性\n`
  SUPPORTED_LOCALES.forEach(locale => {
    const ratio = report.completenessRatio[locale]
    const percentage = (ratio * 100).toFixed(1)
    const missingCount = report.missingTranslations[locale].length
    
    output += `### ${locale}\n`
    output += `- 完整性: ${percentage}% (${report.totalKeys - missingCount}/${report.totalKeys})\n`
    
    if (missingCount > 0) {
      output += `- 遺漏翻譯 (${missingCount}):\n`
      report.missingTranslations[locale].forEach(key => {
        output += `  - \`${key}\`\n`
      })
    } else {
      output += `- ✅ 所有翻譯完整\n`
    }
    output += '\n'
  })
  
  return output
}

/**
 * 為特定路徑添加翻譯
 * @param path 翻譯路徑 (例如: 'common.buttons.submit')
 * @param translations 各語系的翻譯
 */
export function addTranslation(path: string, translations: Partial<TranslationValue>) {
  const keys = path.split('.')
  let current = mergedMessages as any
  
  // 導航到目標位置
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) {
      current[keys[i]] = {}
    }
    current = current[keys[i]]
  }
  
  // 設置翻譯值
  const lastKey = keys[keys.length - 1]
  if (!current[lastKey]) {
    current[lastKey] = {}
  }
  
  // 合併翻譯
  Object.assign(current[lastKey], translations)
}

/**
 * 獲取翻譯路徑的值
 * @param path 翻譯路徑
 * @param locale 語系
 */
export function getTranslation(path: string, locale: SupportedLocale): string {
  const keys = path.split('.')
  let current = mergedMessages as any
  
  for (const key of keys) {
    if (!current[key]) {
      return path // 如果找不到，返回原始路徑
    }
    current = current[key]
  }
  
  if (isTranslationValue(current)) {
    return current[locale] || path
  }
  
  return path
} 